import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Download, Plus } from "lucide-react";
import { useCategories, useProduct, useProducts, fileUrl, productUrl } from "@/lib/api";
import { useApp } from "@/lib/store";
import { FINISH_INFO, SWATCH, fmtLen, fmtW, fieldLabel, fieldsFor } from "@/lib/calc";
import { Lightbox, ProductCard, UnitToggle } from "@/components/site/common";
import { breadcrumbLd, useSeo } from "@/lib/seo";

function Gallery({ images, active, setActive, name }) {
  const [zoom, setZoom] = useState(null);
  const [lb, setLb] = useState(null);
  const onMove = (e) => { const r = e.currentTarget.getBoundingClientRect(); setZoom(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); };
  return (
    <div>
      <button data-testid="gallery-main" onClick={() => setLb(images[active])} onMouseMove={onMove} onMouseLeave={() => setZoom(null)} className="w-full aspect-square card prod-bg flex items-center justify-center overflow-hidden p-10 cursor-zoom-in">
        <img src={images[active]} alt={name} className="max-h-full max-w-full object-contain transition-transform duration-300" style={zoom ? { transform: "scale(1.8)", transformOrigin: zoom } : undefined} />
      </button>
      {images.length > 1 && (
        <div className="flex gap-3 mt-4">
          {images.map((src, i) => (
            <button key={src + i} data-testid={`gallery-thumb-${i}`} onClick={() => setActive(i)} className={`w-20 h-20 card prod-bg p-2 transition-colors ${i === active ? "!border-[var(--brass)]" : ""}`}><img src={src} alt="" className="w-full h-full object-contain" /></button>
          ))}
        </div>
      )}
      <Lightbox src={lb} alt={name} onClose={() => setLb(null)} />
    </div>
  );
}

function SpecTable({ p, v, finish, sys }) {
  const rows = [["Material", p.material], ["Grade / standard", p.grade]];
  fieldsFor(p).forEach((k) => v?.dims?.[k] != null && rows.push([fieldLabel(p, k), fmtLen(v.dims[k], sys)]));
  rows.push(["Weight per piece", v?.unit_weight_kg ? fmtW(+v.unit_weight_kg, sys) : "—"], ["Finish", finish]);
  if (p.thread_type) rows.push(["Thread type", p.thread_type]);
  rows.push(["Packing", `${v?.pcs_per_carton || "—"} pcs / carton · ${v?.cartons_per_pallet || "—"} cartons / pallet`], ["MOQ", p.moq]);
  return (
    <table data-testid="spec-table" className="w-full text-sm tabular">
      <tbody>{rows.map(([k, val]) => <tr key={k} className="border-b border-[var(--stone)]"><th className="text-left font-normal text-[var(--graphite)] py-3 pr-6 w-1/2">{k}</th><td className="py-3">{val}</td></tr>)}</tbody>
    </table>
  );
}

export default function ProductDetail() {
  const { category, slug } = useParams();
  const { data: p, isLoading, isError } = useProduct(slug);
  const { data: cats = [] } = useCategories();
  const { data: related = [] } = useProducts({ category });
  const { add, sys } = useApp();
  const [vi, setVi] = useState(0);
  const [finish, setFinish] = useState("");
  const [img, setImg] = useState(0);
  const cat = cats.find((c) => c.slug === category);
  useEffect(() => { setVi(0); setImg(0); setFinish(p?.finishes?.[0] || ""); }, [p]);
  const v = p?.variants?.[vi];
  const images = p ? Array.from(new Set([...(v?.image ? [v.image] : []), ...p.images])) : [];
  useEffect(() => { setImg(0); }, [vi]);
  useSeo(p?.seo_title || p?.name, p?.seo_description || p?.short_description, p && [
    { "@context": "https://schema.org", "@type": "Product", name: p.name, description: p.description, image: p.images.map((i) => window.location.origin + i), category: cat?.name, material: p.material, brand: { "@type": "Brand", name: "Tijiya Exports" } },
    breadcrumbLd([["Home", "/"], ["Products", "/products"], [cat?.name || category, `/products/${category}`], [p.name, productUrl(p)]]),
  ]);

  if (isLoading) return <div className="wrap py-32 text-sm">Loading…</div>;
  if (isError || !p) return <div className="wrap py-32"><h1 className="text-4xl">Product not found</h1><Link className="link mt-4 inline-block" to="/products/all">Browse all products</Link></div>;
  const q = `/enquiry?product=${p.slug}&variant=${encodeURIComponent(v?.name || "")}&finish=${encodeURIComponent(finish)}`;

  return (
    <div data-testid="product-detail-page">
      <div className="wrap pt-8 text-xs text-[var(--graphite)]" data-testid="breadcrumb">
        <Link to="/" className="hover:text-[var(--brass)]">Home</Link> / <Link to="/products" className="hover:text-[var(--brass)]">Products</Link> / <Link to={`/products/${category}`} className="hover:text-[var(--brass)]">{cat?.name || category}</Link> / <span className="text-[var(--ink)]">{p.name}</span>
      </div>
      <section className="wrap py-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6"><Gallery images={images} active={Math.min(img, images.length - 1)} setActive={setImg} name={p.name} /></div>
        <div className="lg:col-span-6">
          <Link to={`/products/${category}`} className="chip hover:border-[var(--brass)]">{cat?.name}</Link>
          <h1 data-testid="product-title" className="text-4xl sm:text-5xl mt-4">{p.name}</h1>
          <p className="mt-5 text-[var(--graphite)] md:text-lg">{p.description}</p>
          <div className="mt-8"><p className="label">{p.dimension_profile === "STANDARD" ? "Size" : "Variant"}</p>
            <div className="flex flex-wrap gap-2 mt-2">{p.variants.map((x, i) => <button key={x.name} data-testid={`variant-chip-${i}`} data-active={i === vi} onClick={() => setVi(i)} className="chip chip-select">{x.name}</button>)}</div>
          </div>
          <div className="mt-6"><p className="label">Finish</p>
            <div className="flex flex-wrap gap-2 mt-2">{p.finishes.map((f) => <button key={f} data-testid={`finish-chip-${f.replace(/\s+/g, "-").toLowerCase()}`} data-active={f === finish} onClick={() => setFinish(f)} className="chip chip-select">{f}</button>)}</div>
          </div>
          <div className="mt-8 card p-6 lg:sticky lg:top-28" data-testid="product-cta-panel">
            <p className="serif text-2xl">Request a quote for this product</p>
            <p className="text-sm text-[var(--graphite)] mt-1">{v?.name} · {finish}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={q} data-testid="product-request-quote" className="btn btn-brass">Request a Quote</Link>
              <button data-testid="product-add-enquiry" onClick={() => add(p, v?.name, finish)} className="btn btn-outline"><Plus size={16} /> Add to enquiry list</button>
            </div>
          </div>
        </div>
      </section>
      <section className="wrap py-14 grid lg:grid-cols-12 gap-12 border-t border-[var(--stone)]">
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-4"><h2 className="text-3xl">Specifications</h2><UnitToggle /></div>
          <SpecTable p={p} v={v} finish={finish} sys={sys} />
          {p.datasheet_url && <a href={fileUrl(p.datasheet_url)} target="_blank" rel="noreferrer" data-testid="product-datasheet" className="btn btn-outline btn-sm mt-6"><Download size={14} /> Datasheet / drawing (PDF)</a>}
        </div>
        <div className="lg:col-span-5 space-y-10">
          <div><h2 className="text-3xl mb-4">Finishes</h2>
            <div className="space-y-3">{p.finishes.map((f) => <div key={f} className="flex gap-4 items-start"><span className="w-8 h-8 rounded-sm border border-[var(--stone)] shrink-0" style={{ background: SWATCH[f] }} /><div><p className="text-sm font-medium">{f}</p><p className="text-sm text-[var(--graphite)]">{FINISH_INFO[f]}</p></div></div>)}</div>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div><h3 className="text-2xl mb-3">Applications</h3><ul className="space-y-2 text-sm">{p.applications.map((a) => <li key={a} className="flex gap-2"><Check size={14} className="text-[var(--brass)] mt-1 shrink-0" />{a}</li>)}</ul></div>
            <div><h3 className="text-2xl mb-3">Key features</h3><ul className="space-y-2 text-sm">{p.features.map((a) => <li key={a} className="flex gap-2"><Check size={14} className="text-[var(--brass)] mt-1 shrink-0" />{a}</li>)}</ul></div>
          </div>
        </div>
      </section>
      <section className="bg-[var(--paper)] border-t border-[var(--stone)] py-16">
        <div className="wrap">
          <h2 className="text-3xl mb-8">Related products</h2>
          <div data-testid="related-carousel" className="flex gap-6 overflow-x-auto no-scrollbar snap-x">
            {related.filter((r) => r.slug !== p.slug).slice(0, 10).map((r) => <div key={r.slug} className="snap-start shrink-0 w-[250px]"><ProductCard p={r} compact /></div>)}
          </div>
        </div>
      </section>
    </div>
  );
}
